import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import HeaderTwo from "../components/HeaderTwo";
import FooterTwo from "../components/FooterTwo";
import { checkFieldsForProfanity } from "../helper/profanityFilter";

const CouponReviewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token, isAuthenticated } = useAuth();
    const localToken = token || localStorage.getItem("token");

    const [coupon, setCoupon] = useState(null);
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [review, setReview] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [alreadyReviewed, setAlreadyReviewed] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/account-login");
            return;
        }
        fetch(`${process.env.REACT_APP_API_URL}/api/coupons/${id}`)
            .then((res) => res.json())
            .then((data) => setCoupon(data.coupon))
            .catch(() => {});

        fetch(`${process.env.REACT_APP_API_URL}/api/ratings/coupon/${id}`)
            .then((res) => res.json())
            .then((data) => {
                const hasReviewed = (data.ratings || []).some(r => r.user_id === user?.id);
                setAlreadyReviewed(hasReviewed);
            })
            .catch(() => setAlreadyReviewed(false));
    }, [id, isAuthenticated]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) {
            setError("Please select a star rating.");
            return;
        }
        const profanityError = checkFieldsForProfanity({ "Review": review });
        if (profanityError) { setError(profanityError); return; }
        setSubmitting(true);
        setError(null);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ratings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localToken}`,
                },
                body: JSON.stringify({
                    coupon_id: parseInt(id),
                    rating,
                    review: review.trim() || null,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || "Failed to submit review.");
                return;
            }

            setSubmitted(true);
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <HeaderTwo />
            <section className="py-80">
                <div className="container" style={{ maxWidth: 640 }}>
                    {alreadyReviewed ? (
                        <div className="text-center py-80">
                            <span className="text-warning-600 text-64 d-block mb-16">
                                <i className="ph-fill ph-star" />
                            </span>
                            <h4 className="mb-12">Already Reviewed</h4>
                            <p className="text-gray-600 mb-32">
                                You have already submitted a review for{" "}
                                <span className="fw-semibold">{coupon?.title}</span>.
                                Only one review per coupon is allowed.
                            </p>
                            <button
                                onClick={() => navigate(`/coupon/${id}`)}
                                className="btn btn-main rounded-8 px-32 py-14"
                            >
                                Back to Coupon
                            </button>
                        </div>
                    ) : submitted ? (
                        <div className="text-center py-80">
                            <span className="text-main-600 text-64 d-block mb-16">
                                <i className="ph-fill ph-check-circle" />
                            </span>
                            <h4 className="mb-12">Review Submitted!</h4>
                            <p className="text-gray-600 mb-32">
                                Thanks for sharing your feedback on{" "}
                                <span className="fw-semibold">{coupon?.title}</span>.
                            </p>
                            <button
                                onClick={() => navigate(`/coupon/${id}`)}
                                className="btn btn-main rounded-8 px-32 py-14"
                            >
                                Back to Coupon
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-40">
                                <h4 className="mb-8">Write a Review</h4>
                                {coupon && (
                                    <p className="text-gray-600">
                                        You are reviewing:{" "}
                                        <span className="fw-semibold text-heading">{coupon.title}</span>
                                    </p>
                                )}
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="mb-32">
                                    <label className="text-neutral-600 fw-medium d-block mb-12">
                                        Your Rating
                                    </label>
                                    <div className="flex-align gap-8">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onClick={() => setRating(star)}
                                                onMouseEnter={() => setHoveredRating(star)}
                                                onMouseLeave={() => setHoveredRating(0)}
                                                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                                            >
                                                <i
                                                    className={`ph-fill ph-star text-32 ${
                                                        star <= (hoveredRating || rating)
                                                            ? "text-warning-600"
                                                            : "text-gray-300"
                                                    }`}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-32">
                                    <label
                                        htmlFor="review"
                                        className="text-neutral-600 fw-medium d-block mb-8"
                                    >
                                        Review <span className="text-gray-400 fw-normal">(optional)</span>
                                    </label>
                                    <textarea
                                        id="review"
                                        className="common-input rounded-8"
                                        rows={5}
                                        placeholder="Share your experience with this coupon..."
                                        value={review}
                                        onChange={(e) => setReview(e.target.value)}
                                    />
                                </div>

                                {error && (
                                    <p className="text-danger mb-16">{error}</p>
                                )}

                                <div className="flex-align gap-16">
                                    <button
                                        type="submit"
                                        className="btn bg-main-800 text-white hover-bg-main-900 rounded-8 px-32 py-14"
                                        disabled={submitting}
                                    >
                                        {submitting ? "Submitting..." : "Submit Review"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-main rounded-8 px-32 py-14"
                                        onClick={() => navigate(`/coupon/${id}`)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </section>
            <FooterTwo />
        </>
    );
};

export default CouponReviewPage;
